import type { Routes } from '@angular/router';
import { ngxPermissionsGuard } from 'ngx-permissions';

import { authRoutes } from './auth/auth.routes';
import { authGuard } from './auth/guards/auth.guard';
import { ExtraUserPermissions } from './core/constants';
import { resetTopicsGuard } from './core/guards/reset-topics/reset-topics.guard';
import { EnumFeatureFlags } from './user/models/user';

const hiddenForTicketingUser = {
  except: [ExtraUserPermissions.TicketingOnly],
  redirectTo: '/ticket',
};

const nonTicketingUserRouteConfig = {
  canActivate: [authGuard, ngxPermissionsGuard],
  data: {
    permissions: hiddenForTicketingUser,
  },
};

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./dashboard/containers/dashboard-page/dashboard-page.component').then(c => c.DashboardPageComponent),
    ...nonTicketingUserRouteConfig,
  },
  {
    path: 'home',
    loadChildren: () => import('./home/home.routes').then(r => r.homeRoutes),
    ...nonTicketingUserRouteConfig,
  },
  {
    path: 'new',
    loadChildren: () => import('./new/new.routes').then(r => r.newRoutes),
    ...nonTicketingUserRouteConfig,
  },
  {
    path: 'project',
    loadChildren: () => import('./project/project.routes').then(r => r.projectsRoutes),
    ...nonTicketingUserRouteConfig,
    canDeactivate: [resetTopicsGuard],
  },
  {
    path: 'issue',
    loadChildren: () => import('./project/modules/issues/issues.routes').then(r => r.issuesRoutes),
    ...nonTicketingUserRouteConfig,
  },
  {
    path: 'template',
    loadChildren: () => import('./project/modules/templates/templates.routes').then(r => r.templatesRoutes),
    ...nonTicketingUserRouteConfig,
  },
  {
    path: 'process',
    loadChildren: () => import('./project/modules/processes/processes.routes').then(r => r.processesRoutes),
    ...nonTicketingUserRouteConfig,
  },
  {
    path: 'table',
    loadChildren: () => import('./project/modules/tables/tables.routes').then(r => r.tablesRoutes),
    ...nonTicketingUserRouteConfig,
  },
  {
    path: 'monitoring',
    loadChildren: () => import('./project/modules/project-monitoring/project-monitoring.routes').then(r => r.projectMonitoringRoutes),
    ...nonTicketingUserRouteConfig,
  },
  {
    path: 'report',
    loadChildren: () => import('./reports/reports.routes').then(r => r.reportsRoutes),
    ...nonTicketingUserRouteConfig,
  },
  {
    path: 'politician',
    loadChildren: () => import('./politician/politician.routes').then(r => r.politicianRoutes),
    canActivate: [authGuard, ngxPermissionsGuard],
    data: {
      permissions: {
        only: [EnumFeatureFlags.POLITICIANS],
        except: [ExtraUserPermissions.TicketingOnly],
        redirectTo: '/404',
      },
    },
  },
  {
    path: 'user',
    loadChildren: () => import('./user/user.routes').then(r => r.userRoutes),
    canActivate: [authGuard],
  },
  {
    path: 'lari-apps',
    loadChildren: () => import('./lari-apps/lari-apps.routes').then(r => r.lariAppsRoutes),
    ...nonTicketingUserRouteConfig,
  },
  {
    path: 'search',
    loadChildren: () => import('./search/search.routes').then(r => r.searchRoutes),
    ...nonTicketingUserRouteConfig,
  },
  {
    path: 'chat',
    loadComponent: () => import('./chat/containers/chat-page/chat-page.component').then(c => c.ChatPageComponent),
    canActivate: [authGuard, ngxPermissionsGuard],
    data: {
      permissions: {
        only: [EnumFeatureFlags.AI_CHAT],
        redirectTo: '/404',
      },
    },
  },
  {
    path: 'skills',
    loadComponent: () => import('./chat/containers/skills-page/skills-page.component').then(c => c.SkillsPageComponent),
    canActivate: [authGuard, ngxPermissionsGuard],
    data: {
      permissions: {
        only: [EnumFeatureFlags.AI_CHAT],
        redirectTo: '/404',
      },
    },
  },
  {
    path: 'attachment/:id',
    loadComponent: () => import('./attachment/attachment.component').then(c => c.AttachmentComponent),
    canActivate: [authGuard],
  },
  {
    path: 'internal',
    loadChildren: () => import('./internal/internal-items.routes').then(r => r.internalItemsRoutes),
    canActivate: [authGuard, ngxPermissionsGuard],
    data: {
      permissions: {
        only: [EnumFeatureFlags.INTERNAL_LARI_ITEMS],
        redirectTo: '/404',
      },
    },
  },
  {
    path: 'ticket',
    loadChildren: () => import('./ticketing/ticketing.routes').then(r => r.ticketingRoutes),
    canActivate: [authGuard, ngxPermissionsGuard],
    data: {
      permissions: {
        only: [EnumFeatureFlags.TICKETING, ExtraUserPermissions.TicketingOnly],
        redirectTo: '/404',
      },
    },
  },
  ...authRoutes,
  {
    path: '404',
    loadComponent: () => import('./core/containers/page404/page404.component').then(c => c.Page404Component),
  },
  {
    path: '**',
    redirectTo: '404',
  },
];
